import React, {useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import Dialog from "../../base/dialog";
import Divider from "@mui/material/Divider";
import Button from "../../base/button/button";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Form from "../../base/form";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import {OutlinedDropdownForm} from "../../base/dropdown/outlined_dropdown";
import {WarrantyAndReturnRequestStatuses} from "../../../../core/constants/enums";
import {OpenInNew, WarningOutlined} from "@mui/icons-material";
import {IconButton} from "@mui/material";
import {Link} from "react-router-dom";
import {routes} from "../../../../core/models/static/routes/app-routes";
import useAccessLevels from "../../../hooks/use-access-levels";


const schema = Yup.object().shape({
    status: Yup.object().nullable().required(ValidateMessages.required),
    reason: Yup.object().nullable(),
})

const validate = makeValidate(schema);
const required = makeRequired(schema);

const UpsertPartSupportStatusDialog = ({open, setOpen, onSuccess, request, statuses}) => {
    const [submitting, setSubmitting] = useState(false);
    const [initialValues, setInitialValues] = useState({});
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();

    /**
     * Listens for the changes in request and with each change, sets the initialValues of the form data.
     */
    useEffect(() => {
        if (!request) return;
        setInitialValues({status: statuses.find(e => e.id === request?.statusID) ?? ''});
    }, [request])


    /**
     * Changes the status of a request, and if the new status is not same as the status of the request, then calls the
     * api to change it in the server too.
     * @param values {any}
     */
    const changeStatus = (values) => {
        setSubmitting(true);
        Api.changePartSupportRequestStatus(request.id, values.status.id, values.reason?.id).then((response) => {
            if (!isMounted())
                return;
            setSubmitting(false);
            if (response?.resultFlag) {
                setOpen(false);
                onSuccess(request, values.status, values.reason);
            }
        })
    }

    const statusOptions = useMemo(() => {
        const filtered = [WarrantyAndReturnRequestStatuses.waitForOperator, WarrantyAndReturnRequestStatuses.initial];
        return statuses
            ?.filter(e => !filtered.includes(e.id))
            ?.map(e => ({
                value: e,
                label: e.title,
                id: e.id,
            })) ?? []
    }, [statuses]);

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
        >
            <Row className='no-gutters justify-content-center align-items-start w-100'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                        Change Request Status
                    </p>
                </Row>
                <Divider className='divider'/>
                <div className='my-3 w-100'/>
                <Form
                    className='w-100'
                    onSubmit={changeStatus}
                    validate={validate}
                    initialValues={initialValues}
                    render={({values, form}) => {
                        const reasons = values?.status?.reasons ?? [];
                        return (
                            <Row className='no-gutters justify-content-around w-100'>
                                <Row className='no-gutters justify-content-around w-100 mx-4'>
                                    <Col xs={12} className={'my-3 w-100'}>
                                        <p className={'text-color-3 text-sm'}>
                                            Please select the new status and the most relevant reason for the change.
                                        </p>
                                    </Col>
                                    <Col xs={12} className='px-2 mb-4'>
                                        <OutlinedDropdownForm
                                            fontSize={14}
                                            fullWidth={true}
                                            onChange={(e) => {
                                                form.change(e.target.name, e.target.value);
                                                form.change('reason', undefined);
                                            }}
                                            label={'Status'}
                                            name={'status'}
                                            required={!!required.status}
                                            data={statusOptions}
                                        />
                                    </Col>
                                    {
                                        statusOptions.length === 0 &&
                                        operatorAccessLevels.packages.warrantyAndReturn.editConfigurations &&
                                        <Col xs={12} className='px-2 mb-3'>
                                            <p className={'text-sm text-color-3 text-center'}>
                                                <WarningOutlined
                                                    color={'warning'}
                                                    sx={{display: 'inline-block', marginInlineEnd: 0.5}}
                                                />
                                                There are no statuses to select from. Please create a status first.
                                                <IconButton
                                                    color={'paper.primary'}
                                                    component={Link}
                                                    to={routes.warrantyAndReturn.settings.statuses}
                                                    target={'_blank'}
                                                    sx={{display: 'inline-block', marginInlineStart: 0.5}}
                                                    onClick={() => setOpen(false)}
                                                >
                                                    <OpenInNew/>
                                                </IconButton>
                                            </p>
                                        </Col>
                                    }
                                    {
                                        !!values?.status &&
                                        <Col xs={12} className='px-2 mb-4'>
                                            <OutlinedDropdownForm
                                                fontSize={14}
                                                fullWidth={true}
                                                onChange={(e) => form.change(e.target.name, e.target.value)}
                                                label={'Reason for Change'}
                                                name={'reason'}
                                                required={!!required.reason}
                                                data={reasons?.map(e => ({
                                                    value: e,
                                                    label: e.title,
                                                    id: e.id,
                                                })) ?? []}
                                            />
                                        </Col>
                                    }
                                </Row>
                                <div className='my-2 w-100'/>
                                <Divider className='divider'/>
                                <Row className='no-gutters justify-content-between align-items-start w-100'>
                                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                                        <Button
                                            disabled={!open || submitting}
                                            variant={'dialog'}
                                            color={'primary'}
                                            onClick={() => setOpen(false)}
                                            borderRadius={'0 8px 0 8px'}
                                        >
                                            {submitting ? 'submitting...' : 'Close'}
                                        </Button>
                                        <Button
                                            disabled={!open || submitting || request?.statusID === values?.status?.id}
                                            variant={'dialog'}
                                            color={'primary'}
                                            type={'submit'}
                                            borderRadius={'8px 0 8px 0'}
                                        >
                                            {submitting ? 'submitting...' : 'Change'}
                                        </Button>
                                    </Row>
                                </Row>
                            </Row>
                        );
                    }}
                />
            </Row>
        </Dialog>
    );
}

export default UpsertPartSupportStatusDialog;
